// IFRS 18 / Cash Flow card V2 — the new cash-flow screen parts (IFRS.jsx keeps one hook line each; the engine is
// backend/cash_flow_items.py). EN + AR texts live here. The new LINES themselves (FX add-backs, investment property, associates,
// acquisition, gross borrowings, shares …) come from the engine with label / label_ar and render through the existing rows.
import React from "react";
import { asArray } from "@/lib/asArray";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter
import { isBalanced } from "@/lib/tolerances";   // STEP 3 combined · B6c: the one "balanced" rule (mirror of tolerances.is_balanced)

const fmt = (n) => fmtNumber(Number(n ?? 0));

export const CF_TEXT = {
  en: {
    overdraft: "Cash and cash equivalents are shown net of bank overdrafts that form part of cash management (closing {c}, opening {o}).",
    discTitle: "Discontinued operation — net cash flows",
    discBasis: "From the accounts tagged as part of the discontinued operation; the sale of the disposal group is shown in investing activities above.",
    operating: "Operating activities", investing: "Investing activities", financing: "Financing activities",
    warnings: "Warnings — for review; they are not checks and do not change the counts",
    tieHeads: "Balance-sheet heads that moved with no cash-flow line:",
    priorYear: "Prior year:",
  },
  ar: {
    overdraft: "يُعرض النقد وما في حكمه بالصافي بعد السحب على المكشوف الذي يُعد جزءًا من إدارة النقد (الختامي {c}، الافتتاحي {o}).",
    discTitle: "العملية المتوقفة — صافي التدفقات النقدية",
    discBasis: "من الحسابات المحددة كجزء من العملية المتوقفة؛ يظهر بيع مجموعة الاستبعاد ضمن الأنشطة الاستثمارية أعلاه.",
    operating: "الأنشطة التشغيلية", investing: "الأنشطة الاستثمارية", financing: "الأنشطة التمويلية",
    warnings: "تنبيهات — للمراجعة؛ ليست فحوصات ولا تغيّر العدد",
    tieHeads: "بنود في قائمة المركز المالي تحركت دون بند في قائمة التدفقات النقدية:",
    priorYear: "السنة السابقة:",
  },
};

export function cfText(lang, key) {
  return (CF_TEXT[lang] || CF_TEXT.en)[key] || CF_TEXT.en[key] || key;
}

// A3 / B3: the reconciling lines between the net change and the closing cash — FX on cash, the cash of a disposal group held
// for sale. ``Row`` is the screen's own row component (label, amount, prior, testId).
export function CashReconRows({ cf, prior, Row }) {
  const { lang } = useI18n();
  const out = [];
  for (const [key, lk, testId] of [["fx_effect_on_cash", "fx_effect_label", "cf-fx-effect"],
                                   ["hfs_cash_reclassified", "hfs_cash_label", "cf-hfs-cash"]]) {
    if (!(key in (cf || {})) && !(key in (prior || {}))) continue;
    const src = key in (cf || {}) ? cf : prior;
    const label = (lang === "ar" && src[`${lk}_ar`]) || src[lk] || key;
    out.push(<Row key={key} label={label} amount={cf?.[key] ?? 0} prior={prior ? (prior[key] ?? 0) : undefined} testId={testId} />);
  }
  return <>{out}</>;
}

// B2: cash and cash equivalents net of an overdraft that is part of cash management
export function OverdraftNote({ cf }) {
  const { lang } = useI18n();
  const od = cf?.overdraft_in_cash;
  if (!od) return null;
  return (
    <div className="border-b px-4 py-1.5 text-[11px] text-muted-foreground" data-testid="cf-overdraft-note">
      {cfText(lang, "overdraft").replace("{c}", fmt(od.closing)).replace("{o}", fmt(od.opening))}
    </div>
  );
}

// B3 (owner answer 3): the discontinued operation's own net cash flows — only when its accounts are tagged (never zeros);
// PR 46 review CF12: shown when either year has a note (the year without one stays blank)
export function DiscontinuedCashNote({ cf, prior }) {
  const { lang } = useI18n();
  const d = cf?.discontinued_cash_flows;
  const p = prior?.discontinued_cash_flows;
  if (!d && !p) return null;
  return (
    <div className="border-t px-4 py-2 text-sm" data-testid="cf-discontinued-note">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">{cfText(lang, "discTitle")}</div>
      {["operating", "investing", "financing"].map((k) => (
        <div key={k} className="grid grid-cols-[1fr_9rem_9rem] py-0.5" data-testid={`cf-disc-${k}`}>
          <div>{cfText(lang, k)}</div>
          <div className="text-end num-cell">{d ? fmt(d[k]) : ""}</div>
          <div className="text-end num-cell">{p ? fmt(p[k]) : ""}</div>
        </div>
      ))}
      <div className="text-[11px] text-muted-foreground">{cfText(lang, "discBasis")}</div>
    </div>
  );
}

// B2 / B3: WARNINGS (a bargain purchase, the discontinued split not available) — shown on the cash flow and on Validation;
// never a failed check. PR 46 review CF12: ``prior`` = the prior year's warnings, marked "Prior year" (Validation gets them
// from the engine already marked: year "prior")
export function CashFlowWarnings({ warnings, prior, testId = "cf-warnings" }) {
  const { lang } = useI18n();
  const list = [...asArray(warnings), ...asArray(prior).map((w) => ({ ...w, _prior: true }))];
  if (!list.length) return null;
  return (
    <div className="border-b bg-amber-50 px-4 py-2 text-[11px] text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" data-testid={testId}>
      <div className="font-semibold">{cfText(lang, "warnings")}</div>
      {list.map((w, i) => (
        <div key={`${w.key}-${i}`} data-testid={`${testId}-${w._prior ? "prior-" : ""}${w.key}`}>
          {w._prior ? `${cfText(lang, "priorYear")} ` : ""}{(lang === "ar" && w.message_ar) || w.message}
        </div>
      ))}
    </div>
  );
}

// B2: when the tie fails, the heads that moved with no line or non-cash rule
export function TieHeadsNote({ cf }) {
  const { lang } = useI18n();
  const heads = asArray(cf?.tie_heads);
  if (!heads.length || isBalanced(cf?.tie_check)) return null;   // B6c: the one tie rule
  return (
    <div className="border-b bg-red-50 px-4 py-1.5 text-[11px] text-red-800 dark:bg-red-950/30 dark:text-red-200" data-testid="cf-tie-heads">
      {cfText(lang, "tieHeads")}{" "}
      {heads.map((h) => `${h.code} ${(lang === "ar" && h.name_ar) || h.name} (${fmt(h.movement)})`).join(lang === "ar" ? "، " : ", ")}
    </div>
  );
}
